import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

interface Employee {
  dssn: string;
  email: string;
  role: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  employee: Employee | null;
  token: string | null;
  login: (dssn: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('employee_token');
    const storedEmployee = localStorage.getItem('employee_data');
    
    if (storedToken && storedEmployee) {
      setToken(storedToken);
      setEmployee(JSON.parse(storedEmployee));
      setIsAuthenticated(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
    setLoading(false);
  }, []);

  const login = async (dssn: string, password: string) => {
    try {
      const response = await axios.post(`${API_URL}/employees/login`, {
        dssn: dssn,
        password: password
      });
      
      const { token, employee: employeeData } = response.data;
      
      setToken(token);
      setEmployee(employeeData);
      setIsAuthenticated(true);
      
      localStorage.setItem('employee_token', token);
      localStorage.setItem('employee_data', JSON.stringify(employeeData));
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  };

  const logout = () => {
    setToken(null);
    setEmployee(null);
    setIsAuthenticated(false);
    localStorage.removeItem('employee_token');
    localStorage.removeItem('employee_data');
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, employee, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
